import { storeRef } from "@/components/providers/StoreProvider";
import { bookingActions } from "@/lib/redux/features/booking/reduce";
import { esimActions } from "@/lib/redux/features/esim/reduce";
import {
  EsimBookingDraft,
  EsimCountry,
  EsimPackage,
  EsimSelectedPackage,
} from "@/lib/redux/features/esim/types";
import { thunks } from "@/lib/redux/thunks";
import { getCustomerCode } from "@/utils/booking/getCustomerCode";
import {
  getEsimPackageBaseTotal,
  getEsimPackageFeeMakeUpTotal,
} from "@/utils/esim/esimPackagePrice";
import dayjs from "dayjs";

type EsimOrderLine = {
  ggg_plan_id: string;
  amount: number;
  price: number;
  /** Country the customer chose the package for (a package may cover many countries). */
  country: EsimCountry;
};
type EsimOrderContact = { email?: string; phone?: string; fullName?: string };

/** DOP-816 — create: stringified array; checkout: object */
export const buildEsimMetaData = (
  orders: EsimOrderLine[],
  contact?: Partial<{ email: string; phone: string; fullName: string }>,
) => ({
  orders,
  ...(contact?.email || contact?.phone || contact?.fullName ? { contact } : {}),
});

export const getEsimCreatedOrder = (payload: unknown) => {
  const p = payload as Record<string, unknown> | null | undefined;
  if (!p) return undefined;
  return (p.order as Record<string, unknown> | undefined) || p.data || p;
};

export const getEsimOrderCode = (order: unknown): string | undefined => {
  if (!order || typeof order !== "object") return undefined;
  const o = order as Record<string, unknown>;
  if (typeof o.orderCode === "string" && o.orderCode) return o.orderCode;
  const nested = o.order as { orderCode?: string } | undefined;
  const data = o.data as { orderCode?: string; order?: { orderCode?: string } } | undefined;
  return nested?.orderCode || data?.orderCode || data?.order?.orderCode;
};

export const isValidEsimOrderCode = (code?: string | null): code is string =>
  !!code && code !== "undefined" && code !== "null";

export const parseOrderAmount = (v: unknown) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

export const resolveEsimOrderCode = (draft?: EsimBookingDraft | null) =>
  getEsimOrderCode(draft?.order) ||
  (typeof localStorage !== "undefined"
    ? localStorage.getItem("current_order_code") || undefined
    : undefined);

const clearOtherBookingStorage = () => {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem("booking_flight");
  localStorage.removeItem("flights");
  localStorage.removeItem("hotel");
};

/** `startedAt`: start of the hold countdown (the order's createdDate when restoring an order), default now. */
const persistEsimDraft = (draft: EsimBookingDraft, orderCode?: string, startedAt?: string) => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem("esim_booking", JSON.stringify(draft));
  localStorage.setItem("current_time_session", startedAt || dayjs().toString());
  if (orderCode) localStorage.setItem("current_order_code", orderCode);
  else localStorage.removeItem("current_order_code");
  clearOtherBookingStorage();
};

const dispatchEsimDraft = (selected: EsimSelectedPackage, draft: EsimBookingDraft) => {
  storeRef.current?.dispatch(bookingActions.setModalTimeOut(false));
  storeRef.current?.dispatch(esimActions.selectPackage(selected));
  storeRef.current?.dispatch(esimActions.setBookingDraft(draft));
};

/** Persist + dispatch an eSIM booking draft restored from an existing order (preview order). */
export const applyEsimBookingSession = (draft: EsimBookingDraft, orderCode: string, createdDate: string) => {
  persistEsimDraft(draft, orderCode, createdDate);
  dispatchEsimDraft(draft.selected, draft);
};

/**
 * Verify-before-checkout fail (409) hoặc lỗi hệ thống (500) đều khiến BE set
 * `order.bookingStatus = FAIL` vĩnh viễn trước khi trả lỗi (xem docs/planBuild/api_guide.md mục 5)
 * — gọi lại checkout trên orderCode cũ chỉ dính thêm lỗi 1101, không verify lại được nữa. Phải xoá
 * sạch draft/orderCode phía FE để bắt buộc tạo order MỚI, không giữ lại gì của phiên lỗi này.
 */
export const clearEsimBookingSession = () => {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("esim_booking");
    localStorage.removeItem("current_order_code");
    localStorage.removeItem("current_time_session");
  }
  storeRef.current?.dispatch(esimActions.selectPackage(null));
  storeRef.current?.dispatch(esimActions.setBookingDraft(null));
};

/** Mua ngay — draft only; order created after contact (flight flow). */
export const handleChooseEsim = (
  { pkg, country, quantity = 1 }: { pkg: EsimPackage; country: EsimCountry; quantity?: number },
  callback: () => void,
) => {
  const selected = { package: pkg, country, quantity };
  const draft = { selected };
  persistEsimDraft(draft);
  dispatchEsimDraft(selected, draft);
  callback();
};

export const createEsimOrder = async (
  selected: EsimSelectedPackage,
  contact?: EsimOrderContact,
) => {
  const { package: pkg, country, quantity } = selected;
  const profile = storeRef.current?.getState().user.profile.data;
  const baseTotal = getEsimPackageBaseTotal(pkg, quantity);
  const feeMakeUpTotal = getEsimPackageFeeMakeUpTotal(pkg, quantity);
  const metaPayload = buildEsimMetaData(
    [{
      ggg_plan_id: pkg.gggPlanId,
      amount: quantity,
      price: pkg.price ?? 0,
      country,
    }],
    contact,
  );

  const res = await storeRef
    .current?.dispatch(
      thunks.flight_create_order({
        body: {
          TYPE: "JSON",
          partnerId: process.env.NEXT_PUBLIC_AUTHEN_API_CLIENT_ID,
          customerCode:
            profile?.customerCode || getCustomerCode({ phone: contact?.phone || "" }),
          orderDate: dayjs().format("YYYY-MM-DD"),
          productType: "ESIM",
          totalAmount: baseTotal + feeMakeUpTotal,
          discountAmount: 0,
          serviceAmount: 0,
          feeAmount: feeMakeUpTotal,
          payWithPoint: 50,
          voucherCode: "",
          metaData: JSON.stringify([metaPayload]),
          itinData: "{}",
          partnerRequestId: `esim-${pkg.gggPlanId}-${Date.now()}`,
        },
      }),
    )
    .unwrap();

  const createdOrder = getEsimCreatedOrder(res);
  const orderCode = getEsimOrderCode(createdOrder);
  if (!orderCode) throw new Error("Create order response missing orderCode");

  const bookingDraft: EsimBookingDraft = {
    selected,
    order: { ...(createdOrder as object), orderCode },
  };

  persistEsimDraft(bookingDraft, orderCode);
  dispatchEsimDraft(selected, bookingDraft);

  return { orderCode, createdOrder, bookingDraft };
};
